import React, { useEffect, useState } from "react";
import AdminLayout from "./AdminLayout";
import Toast from "../../components/Common/Toast";
import commonService from "../../services/otherServices";
import { getErrorMessage } from "../../services/apiClient";
import { API_CONFIG } from "../../utils/constants";
import { Settings2, TriangleAlert, Monitor, Zap, Check, ExternalLink, ListChecks } from "lucide-react";

type SendMethod = "automation" | "api";

// BASE_URL already ends in "/api/v1/" - matches the backend route exactly
// (app/api/v1/endpoints/salesforce_api_endpoint.py, mounted at /salesforce-api).
const loginUrlForAccount = (accountNumber: number) =>
  `${API_CONFIG.BASE_URL}salesforce-api/login?account=${accountNumber}`;

const AdminSalesforceSendMethodPage: React.FC = () => {
  const [current, setCurrent] = useState<SendMethod | null>(null);
  const [selected, setSelected] = useState<SendMethod | null>(null);
  const [loading, setLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  const [accounts, setAccounts] = useState<{ account_number: number; email: string }[]>([]);

  const [toast, setToast] = useState<{ type: "success" | "error"; message: string } | null>(null);

  const fetchCurrent = async () => {
    setLoading(true);

    try {
      const res = await commonService.getSalesforceSendMethod();
      const method: SendMethod = res.data?.send_method || "automation";
      setCurrent(method);
      setSelected(method);
    } catch (error) {
      setToast({ type: "error", message: getErrorMessage(error, "Failed to load current send method") });
    } finally {
      setLoading(false);
    }
  };

  const fetchAccounts = async () => {
    try {
      const res = await commonService.getSalesforceAccounts();
      setAccounts(res.data?.data || []);
    } catch (error) {
      setToast({ type: "error", message: getErrorMessage(error, "Failed to load Salesforce accounts") });
    }
  };

  useEffect(() => {
    fetchCurrent();
    fetchAccounts();
  }, []);

  const handleSave = async () => {
    if (!selected || selected === current) return;

    setIsSaving(true);

    try {
      const res = await commonService.updateSalesforceSendMethod(selected);
      setCurrent(res.data?.send_method || selected);
      setToast({
        type: "success",
        message: `Salesforce send method is now "${selected === "api" ? "API" : "Automation"}" for every campaign`,
      });
    } catch (error) {
      setToast({ type: "error", message: getErrorMessage(error, "Failed to update send method") });
    } finally {
      setIsSaving(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div style={{ padding: "28px" }}>Loading...</div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div style={{ padding: "28px", maxWidth: "760px" }}>
        <div style={{ marginBottom: "22px" }}>
          <div style={{ fontSize: "26px", fontWeight: 800, color: "#111827", display: "flex", alignItems: "center", gap: "10px" }}>
            <Settings2 size={24} color="#4338ca" />
            Salesforce Send Method
          </div>
          <div style={{ fontSize: "13px", color: "#6b7280", marginTop: "4px" }}>
            One global switch — applies to every campaign, for every user. This is not a per-user or
            per-account setting.
          </div>
        </div>

        <div
          style={{
            display: "flex",
            gap: "10px",
            alignItems: "flex-start",
            background: "#fef3c7",
            border: "1px solid #fde68a",
            borderRadius: "12px",
            padding: "14px 16px",
            marginBottom: "20px",
          }}
        >
          <TriangleAlert size={18} color="#b45309" style={{ flexShrink: 0, marginTop: "2px" }} />
          <div style={{ fontSize: "13px", color: "#92400e", lineHeight: 1.5 }}>
            Changing this affects <strong>every campaign currently in flight</strong> the next time it sends,
            not just new ones. Currently active: <strong>{current === "api" ? "API" : "Automation"}</strong>.
          </div>
        </div>

        <div
          style={{
            background: "white",
            border: "1px solid #e5e7eb",
            borderRadius: "16px",
            padding: "20px",
            marginBottom: "22px",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "10px" }}>
            <ListChecks size={18} color="#4338ca" />
            <div style={{ fontWeight: 700, fontSize: "15px", color: "#111827" }}>
              Before selecting API — one-time setup per account
            </div>
          </div>

          <div style={{ fontSize: "13px", color: "#6b7280", lineHeight: 1.6, marginBottom: "14px" }}>
            The API path needs a one-time Salesforce login for <strong>each account</strong> you plan to
            use — it isn't shared with the Automation accounts' saved passwords. Do this <strong>before</strong>{" "}
            switching to API, or real campaigns using an account that hasn't logged in yet will fail.
          </div>

          <ol style={{ fontSize: "13px", color: "#374151", lineHeight: 1.8, paddingLeft: "20px", marginBottom: "14px" }}>
            <li>Click "Open Login" below for the account you want to activate.</li>
            <li>Log in to Salesforce as that account's own identity when redirected.</li>
            <li>
              You'll land on a page showing{" "}
              <code style={{ background: "#f3f4f6", padding: "1px 6px", borderRadius: "5px", fontSize: "12px" }}>
                {'{"message": "Salesforce API login successful..."}'}
              </code>{" "}
              — that confirms it's activated.
            </li>
            <li>Repeat for every account you want available before relying on API for real campaigns.</li>
          </ol>

          {accounts.length === 0 ? (
            <div style={{ fontSize: "13px", color: "#9ca3af" }}>No Salesforce accounts are configured yet.</div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
              {accounts.map((acc) => (
                <div
                  key={acc.account_number}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: "12px",
                    padding: "10px 14px",
                    borderRadius: "10px",
                    background: "#f9fafb",
                    border: "1px solid #f3f4f6",
                  }}
                >
                  <div style={{ fontSize: "13px", color: "#111827" }}>
                    <strong>Account {acc.account_number}</strong> — {acc.email}
                  </div>

                  <a
                    href={loginUrlForAccount(acc.account_number)}
                    target="_blank"
                    rel="noreferrer"
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "6px",
                      fontSize: "12px",
                      fontWeight: 600,
                      color: "#4338ca",
                      background: "#eef2ff",
                      padding: "6px 12px",
                      borderRadius: "8px",
                      textDecoration: "none",
                      whiteSpace: "nowrap",
                    }}
                  >
                    Open Login
                    <ExternalLink size={13} />
                  </a>
                </div>
              ))}
            </div>
          )}
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px", marginBottom: "22px" }}>
          <MethodCard
            icon={<Monitor size={20} color="#0369a1" />}
            title="Automation"
            description="Existing browser-automation pipeline (Playwright/AdsPower) — logs in and drives Salesforce's UI directly. Unchanged, default behavior."
            active={selected === "automation"}
            onClick={() => setSelected("automation")}
          />
          <MethodCard
            icon={<Zap size={20} color="#7c3aed" />}
            title="API"
            description="Direct Salesforce REST API — no browser, no login session. Requires the OAuth Connected App to already be authorized."
            active={selected === "api"}
            onClick={() => setSelected("api")}
          />
        </div>

        <button
          onClick={handleSave}
          disabled={isSaving || !selected || selected === current}
          style={{
            ...primaryBtn,
            opacity: isSaving || !selected || selected === current ? 0.5 : 1,
            cursor: isSaving || !selected || selected === current ? "not-allowed" : "pointer",
          }}
        >
          <Check size={15} />
          {isSaving ? "Saving..." : "Save"}
        </button>
      </div>

      {toast && <Toast type={toast.type} message={toast.message} onClose={() => setToast(null)} />}
    </AdminLayout>
  );
};

const MethodCard: React.FC<{
  icon: React.ReactNode;
  title: string;
  description: string;
  active: boolean;
  onClick: () => void;
}> = ({ icon, title, description, active, onClick }) => (
  <div
    onClick={onClick}
    style={{
      cursor: "pointer",
      background: "white",
      borderRadius: "16px",
      border: active ? "2px solid #4338ca" : "1px solid #e5e7eb",
      boxShadow: active ? "0 6px 20px rgba(67,56,202,0.15)" : "none",
      padding: "20px",
      display: "flex",
      flexDirection: "column",
      gap: "10px",
    }}
  >
    <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
      {icon}
      <div style={{ fontWeight: 700, fontSize: "15px", color: "#111827" }}>{title}</div>
      {active && (
        <span
          style={{
            marginLeft: "auto",
            fontSize: "11px",
            fontWeight: 700,
            color: "#4338ca",
            background: "#eef2ff",
            padding: "3px 9px",
            borderRadius: "999px",
          }}
        >
          Selected
        </span>
      )}
    </div>
    <div style={{ fontSize: "13px", color: "#6b7280", lineHeight: 1.5 }}>{description}</div>
  </div>
);

const primaryBtn: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: "8px",
  background: "#4338ca",
  color: "#fff",
  border: "none",
  borderRadius: "10px",
  padding: "11px 18px",
  fontWeight: 600,
  fontSize: "14px",
};

export default AdminSalesforceSendMethodPage;
